"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { useMedia } from "react-use";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNewManualQuestion } from "../api/use-new-manual-question";
import useManualQuestionModal from "../hooks/use-manual-question-modal";
import useManualQuestionSidebar from "../hooks/use-manual-question-sidebar";

const formSchema = z.object({
  question: z.string().min(1, { message: "Required" }),
  answer: z.string().optional().default(""),
});

export const NewManualQuestionForm = () => {
  const params = useParams<{ studySetId: string }>();
  const id = params.studySetId;

  const isMobile = useMedia("(max-width: 1024px)", false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: "",
      answer: "",
    },
  });

  const { onClose: onCloseModal } = useManualQuestionModal();
  const { onClose: onCloseSidebar } = useManualQuestionSidebar();

  const newQuestionMutation = useNewManualQuestion({
    id,
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    newQuestionMutation.mutate({
      id,
      question: values.question,
      answer: values.answer,
    });
    isMobile && onCloseModal();
    form.reset();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Question</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ex: What is the capital of France?"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Answer</FormLabel>
              <FormControl>
                <Textarea
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      form.handleSubmit((data) => {
                        onSubmit(data);
                      })();
                    }
                  }}
                  minRows={3}
                  maxRows={6}
                  className="resize-none"
                  placeholder="Write the correct answer here..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-y-4 w-full">
          <Button type="submit">Create Question</Button>
          {isMobile ? (
            <Button onClick={onCloseModal} variant="ghost" type="button">
              Done
            </Button>
          ) : (
            <Button onClick={onCloseSidebar} variant="outline" type="button">
              Done
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};
